import {
  Component,
  ComponentRef,
  ElementRef,
  OnDestroy,
  OnInit,
  ViewChild,
  ViewContainerRef,
  AfterViewInit,
  Output,
  EventEmitter
} from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { AwallComponentEvent } from '../../models/awallComponentEvent';
import { SettingsService } from '../../services/settings.service';
import { ProgramIncrement } from '../../models/programIncrement';
import { Subscription, forkJoin, fromEvent, Observable, Observer } from 'rxjs';
// eslint-disable-next-line max-len
import { ProgramService } from '../../services/program.service';
import { ProgramIncrementService } from '../../services/programIncrement.service';
import { SettingsEventType } from '../../models/enums/settingsEventType';
import { MpcBoardTeamComponent } from '../../components/mpc-board/mpc-board-team/mpc-board-team.component';
import { Program } from '../../models/program';
import { AcknowledgeDialogComponent } from '../../components/dialogs/acknowledge-dialog/acknowledge-dialog.component';
import { TranslateService } from '@ngx-translate/core';
import { ComponentEvent } from '../../models/enums/componentEvent';
import { DynamicElementService } from '../../services/dynamicElement.service';
import { DependencyDrawingService } from '../../services/dependency-drawing.service';
import { DependencyEditmodeService } from '../../services/dependency-editmode.service';
import { SynchronizationService } from '../../services/synchronization.service';
import { Issue } from '../../models/issue';
import { Sprint } from '../../models/sprint';
import { Status } from '../../models/status';
import { BoardData } from '../../models/boardData';
import { BaseBoardComponent } from '../baseBoard';
import { Listener, CommunicationService } from '../../services/communication.service';
import { ProjectService } from '../../services/project.service';


@Component({
  selector: 'app-mpc-board-view',
  templateUrl: './mpc-board-view.component.html',
  styleUrls: ['./mpc-board-view.component.scss']
  })
export class MpcBoardViewComponent extends BaseBoardComponent implements OnInit, OnDestroy, AfterViewInit, Listener<AwallComponentEvent> {
  @ViewChild('mpcBoardTeamInsertionPoint', { read: ViewContainerRef }) mpcBoardTeamInsertionPoint: ViewContainerRef;
  program: Program;
  programTeamsKeys: string[];
  availableTeamsKeys: string[];
  selectedTeamKey: string;
  programIncrement: ProgramIncrement;
  public static readonly componentId = 'MpcBoardViewComponent';
  private teamComponentMap = new Map<string, ComponentRef<MpcBoardTeamComponent>>();
  DependencyEditmodeService = DependencyEditmodeService;
  DependencyDrawingService = DependencyDrawingService;
  @ViewChild('maincontainer', { static: true }) maincontainer: ElementRef;
  private dataHasBeenLoaded = false;
  @Output() loadingDataCompleted = new EventEmitter<void>();

  constructor(
    private matDialog: MatDialog,
    private programService: ProgramService,
    private programIncrementService: ProgramIncrementService,
    private projectService: ProjectService,
    private settingsService: SettingsService,
    private communicationService: CommunicationService,
    protected translate: TranslateService,
    public dependencyDrawingService: DependencyDrawingService,
    public dependencyEditmodeService: DependencyEditmodeService,
    private syncService: SynchronizationService
  ) {
    super();
  }

  ngOnInit() {
    this.communicationService.registerComponent(MpcBoardViewComponent.componentId, this);
    // Add Subscription for changing program-increment
    this.subscriptions.push(this.settingsService.getUpdateObservable().subscribe(settingsEventType => {
      if (settingsEventType === SettingsEventType.PROGRAM_INCREMENT_CHANGED) {
        this.loadData();
      }
    }));
    this.subcribeToWindowResizeEvent();
  }

  ngAfterViewInit() {
    const container = <HTMLElement>document.querySelector('#mpc-main-container');
    this.dependencyDrawingService.initJSPlumb(container);
  }

  ngOnDestroy(): void {
    this.communicationService.unregisterComponent(MpcBoardViewComponent.componentId);
    for (const ev of this.subscriptions) {
      ev.unsubscribe();
    }

    this.dependencyDrawingService.clearAll();
    DependencyEditmodeService.editModeActive = false;
  }

  // TODO experimental code, remove later
  public toggleShowDependencies() {
    const dependenciesVisible = !this.dependencyDrawingService.dependenciesVisible;
    this.dependencyDrawingService.setShowDependencies(dependenciesVisible);
    const event = new AwallComponentEvent(ComponentEvent.DEPENDENCY_VISIBILITY_CHANGED, dependenciesVisible);
    this.communicationService.sendToAll(event);
  }

  /**
   * Listen to window.resize event to redraw dependencies. Also triggers on zoom-in/zoom-out
   */
  subcribeToWindowResizeEvent(): void {
    this.subscriptions.push(fromEvent(window, 'resize').subscribe(() => {
      this.dependencyDrawingService.repaintAll();
    }));
  }

  private loadData() {
    const programId = this.settingsService.getProgramId();
    const programIncrementId = this.settingsService.getProgramIncrementId(programId);

    if (!programId || !programIncrementId) {
      console.log('MPC-Board cant load data. ProgramId or ProgramIncrementId undefined');
      return;
    }
    // Load programIncrement and available projects first
    forkJoin([
      this.programIncrementService.getProgramIncrementById(programIncrementId, programId),
      this.projectService.getKeysOfAvailableProjects(),
      this.programService.getTeamsByProgram(programId)
    ]).subscribe(([programIncrement, allTeamKeys, teams]) => {
      this.mpcBoardTeamInsertionPoint.clear();
      this.programIncrement = programIncrement;
      this.programTeamsKeys = teams.filter(p => p != null).map(p => p.key);
      this.initAvailableTeamsKeys(allTeamKeys);
      this.createAllTeamComponentsForProgram();
      this.showHeadersForFirstTeam();
      this.syncService.joinPersistenceRoom('MPC-Program-' + programId);
    });
  }

  /**
   * Sets the 'availableTeamKeys' Collection so that this contains all known teams minus
   * the ones that are already in the current program.
   */
  private initAvailableTeamsKeys(allTeamKeys: string[]) {
    this.availableTeamsKeys = allTeamKeys;
    this.programTeamsKeys.forEach(team => this.removeFromAvailableTeams(team));
    this.availableTeamsKeys.sort();
  }

  createAllTeamComponentsForProgram(): void {
    for (const team of this.programTeamsKeys) {
      this.createTeamComponent(team);
    }
  }

  createTeamComponent(teamKey: string): void {
    const componentRef: ComponentRef<MpcBoardTeamComponent> = this.mpcBoardTeamInsertionPoint.createComponent(MpcBoardTeamComponent);
    const mpcBoardTeam: MpcBoardTeamComponent = componentRef.instance;
    mpcBoardTeam.programIncrement = this.programIncrement;
    mpcBoardTeam.teamKey = teamKey;
    mpcBoardTeam.loadingDataCompleted.subscribe((boardTeamKey) => {
      this.onTeamBoardIsReady(boardTeamKey);
    });
    this.teamComponentMap.set(teamKey, componentRef);

    this.removeFromAvailableTeams(teamKey);
    this.selectedTeamKey = undefined;
  }

  private showHeadersForFirstTeam() {
    const firstTeamKey = this.programTeamsKeys[0];
    if (this.teamComponentMap.has(firstTeamKey)) {
      const firstTeam: MpcBoardTeamComponent = this.teamComponentMap.get(firstTeamKey).instance;
      firstTeam.showHeader = true;
    }
  }

  private removeFromAvailableTeams(teamKey: string): void {
    const index = this.availableTeamsKeys.findIndex(x => x === this.selectedTeamKey);
    if (index > -1) {
      this.availableTeamsKeys.splice(index, 1);
    }
  }

  private addToAvailableTeams(teamKey: string): void {
    this.availableTeamsKeys.push(teamKey);
    this.availableTeamsKeys.sort();
  }

  onCommunicationEvent(event: AwallComponentEvent): void {
    super.onCommunicationEvent(event);

    switch (event.name) {
      case ComponentEvent.MPCBOARD_REMOVE_TEAM: {
        const teamKey = <string>event.value['teamKey'];
        this.removeTeamFromProject(teamKey);
        break;
      }
    }
  }

  addTeamToProgram(): void {
    if (this.programTeamsKeys.includes(this.selectedTeamKey)) {
      return;
    }
    this.programService.addTeamToProgram(this.settingsService.getProgramId(), this.selectedTeamKey).subscribe({
      next: () => {
        this.programTeamsKeys.push(this.selectedTeamKey);
        this.createTeamComponent(this.selectedTeamKey);
      },
      error: (error) => {
        console.log(error);
        if (error.status === 409) {
          this.openUnableToAssignTeamDialog();
        }
      }
    });
  }

  removeTeamFromProject(teamKey: string): void {
    if (this.programTeamsKeys.includes(this.selectedTeamKey)) {
      return;
    }
    this.programService.removeTeamFromProgram(this.settingsService.getProgramId(), teamKey).subscribe({
      next: () => {
        const projectIndex = this.programTeamsKeys.findIndex(p => p === teamKey);
        this.programTeamsKeys.slice(projectIndex, 1);
        const index = this.mpcBoardTeamInsertionPoint.indexOf(this.teamComponentMap.get(teamKey).hostView);
        this.mpcBoardTeamInsertionPoint.remove(index);
        this.addToAvailableTeams(teamKey);
        this.teamComponentMap.delete(teamKey);
      },
      error: () => {
        this.openUnableToAssignTeamDialog();
      }
    });
  }

  openUnableToAssignTeamDialog(): void {
    const title = this.translate.instant('actionview.mpc-board.unableToAssignTeamDialogTitle');
    const message = this.translate.instant('actionview.mpc-board.unableToAssignTeamDialogText');
    const okText = this.translate.instant('actionview.mpc-board.unableToAssignTeamDialogOkText');

    this.matDialog.open(AcknowledgeDialogComponent, {
      data: { title: title, message: message, buttonTextConfirm: okText }
    });
  }

  getStatuses(): Status[] {
    const boardStatuses = new Map<number, Status>();
    this.teamComponentMap.forEach((teamBoard) => {
      teamBoard.instance.statuses.forEach(status => {
        boardStatuses.set(status.id, status);
      });
    });
    return Array.from(boardStatuses.values());
  }

  getProjectKeys(): string[] {
    return Array.from(this.teamComponentMap.keys());
  }

  getSprints(): Sprint[] {
    const allSprints: Sprint[] = [];
    this.teamComponentMap.forEach((value) => {
      allSprints.push(...value.instance.sprints);
    });
    return allSprints;
  }

  getIssues(): Issue[] {
    const allIssues: Issue[] = [];
    this.teamComponentMap.forEach((teamBoard) => {
      teamBoard.instance.mpcSprintBoards.forEach(sprintBoard => {
        allIssues.push(...sprintBoard.issues);
      });
    });
    return allIssues;
  }

  onTeamBoardIsReady(teamKey: string): void {
    this.teamComponentMap.get(teamKey)['teamBoardReady'] = true;
    const allReady = Array.from(this.teamComponentMap.values()).every(teamBoard => (teamBoard['teamBoardReady']));

    if (allReady) {
      this.dataHasBeenLoaded = true;
      this.loadingDataCompleted.emit();
    }
  }

  /**
   * Returns BoardData of this board as Observable. If the board
   * has already finish loading its data, the Observable will be completed immediatly.
   * If not, we wait for the board to finish loading and then serve and complete the data.
   */
  getBoardData(): Observable<BoardData> {
    const localThis = this;

    function deliver(observer: Observer<BoardData>): void {
      const data = new BoardData(
        localThis.getStatuses(),
        localThis.getProjectKeys(),
        localThis.getSprints(),
        localThis.getIssues()
      );
      observer.next(data);
      observer.complete();
    }

    function sequenceSubscriber(observer: Observer<BoardData>) {
      if (localThis.dataHasBeenLoaded) {
        deliver(observer);
      }
      else {
        localThis.loadingDataCompleted.subscribe(() => {
          deliver(observer);
        });
      }
      return { unsubscribe() { } };
    }
    return new Observable(sequenceSubscriber);
  }

  toggleEditMode(): void {
    this.dependencyEditmodeService.toggleEditMode();
  }
}
